import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Paper,
  useTheme,
  CircularProgress,
  Chip,
  alpha,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import { useSelector, useDispatch } from "react-redux";
import { fetchMunicipalities } from "../store/slices/municipalitySlice";
import BulgarianMap from "../components/municipal/BulgarianMap";
import {
  InfoOutlined,
} from "@mui/icons-material";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const HomePage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const {
    municipalities = [],
    loading,
    error,
  } = useSelector((state) => state.municipalities);
  const { nationalAverages } = useSelector((state) => state.data);

  useEffect(() => {
    dispatch(fetchMunicipalities());
  }, [dispatch]);

  // Официални данни от изследването
  const officialStatistics = {
    totalChitalishta: 3597, // Общ брой читалища в изследването
    totalStaff: 12008, // Работници в читалищата (НСИ, 2022)
    totalSubsidy2025: 152000000, // Държавна субсидия за 2025 г.
    secretariesHigherEducationPercent: 24.1, // Секретари с висше (НСИ, 2022)
    librariesWithoutNewBooksPercent: 29.48, // Библиотеки без нови книги (Регистър, 2022)
    chitalishtaNoTrainingPercent: 70.54, // Читалища без обучение (Регистър, 2023)
  };

  const keyStatistics = [
    {
      title: "Държавна субсидия за читалищата",
      value: "152 млн. лв.",
      description: "За 2025 г. от държавния бюджет",
      icon: "💰",
      source: "Министерство на финансите",
      year: "2025",
      color: theme.palette.primary.main,
    },
    {
      title: "Работещи в читалищата в България",
      value: "12 008",
      description: "НСИ, 2022",
      icon: "👥",
      source: "НСИ",
      year: "2022",
      color: theme.palette.secondary.main,
    },
    {
      title: "Секретари с висше образование",
      value: "24.1%",
      description: "НСИ, 2022",
      icon: "🎓",
      source: "НСИ",
      year: "2022",
      color: theme.palette.success.main,
    },
    {
      title: "Селски библиотеки без нови книги",
      value: "29.48%",
      description: "Регистър на обществените библиотеки, 2022",
      icon: "📚",
      source: "Регистър",
      year: "2022",
      color: theme.palette.warning.main,
    },
    {
      title: "Читалища без обучение",
      value: "70.54%",
      description: "Регистър на читалищата, 2023",
      icon: "📊",
      source: "Регистър",
      year: "2023",
      color: theme.palette.error.main,
    },
  ];

  if (loading && municipalities.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{ animation: `${fadeIn} 0.5s ease-out`, px: { xs: 2, sm: 3, md: 4 } }}
      >
        {/* Заглавие и описание */}
        <Box sx={{ mb: 5, textAlign: "center" }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 2,
              fontSize: { xs: "1.45rem", sm: "1.75rem", md: "1.85rem" },
            }}
          >
            Общински барометър на читалищата
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: "800px", mx: "auto", mb: 2 }}
          >
            за{" "}
            <strong>
              {officialStatistics.totalChitalishta.toLocaleString("bg-BG")}{" "}
              читалища
            </strong>{" "}
            и{" "}
            <strong>
              {officialStatistics.totalStaff.toLocaleString("bg-BG")} читалищни
              дейци
            </strong>{" "}
            в България.
          </Typography>
        </Box>

        {/* Карта - на цяла ширина */}
        <Box sx={{ mb: 6 }}>
          {error ? (
            <Paper
              elevation={3}
              sx={{ p: 4, textAlign: "center", borderRadius: 2 }}
            >
              <Typography color="error" gutterBottom>
                Грешка при зареждане на геоданни: {error}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Картата ще използва демо данни за тестване.
              </Typography>
            </Paper>
          ) : (
            <Box
              sx={{
                width: "100%",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: theme.shadows[2],
              }}
            >
              <BulgarianMap municipalities={municipalities} />
            </Box>
          )}
        </Box>

        {/* Ключови официални статистики */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontWeight: 600, mb: 4, textAlign: "center" }}
          >
            Ключови данни за читалищата в България
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 2,
              "& > *": {
                flex: {
                  xs: "1 0 100%",
                  sm: "1 0 calc(50% - 16px)",
                  md: "1 0 calc(20% - 16px)",
                },
                maxWidth: {
                  xs: "100%",
                  sm: "calc(50% - 16px)",
                  md: "calc(20% - 16px)",
                },
                minWidth: { xs: "100%", sm: "calc(50% - 16px)", md: "200px" },
              },
            }}
          >
            {keyStatistics.map((stat, index) => (
              <Paper
                key={index}
                elevation={1}
                sx={{
                  p: { xs: 2, sm: 2, md: 2 },
                  borderRadius: 2,
                  borderLeft: `3px solid ${stat.color}`,
                  backgroundColor: alpha(stat.color, 0.03),
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: theme.shadows[2],
                  },
                }}
              >
                {/* Заглавие и икона */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <Box
                    sx={{
                      backgroundColor: alpha(stat.color, 0.1),
                      borderRadius: "50%",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontSize: "1.25rem" }}>
                      {stat.icon}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.primary,
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      lineHeight: 1.2,
                    }}
                  >
                    {stat.title}
                  </Typography>
                </Box>

                {/* Стойност */}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: stat.color,
                    fontSize: { xs: "1.75rem", sm: "1.5rem", md: "1.75rem" },
                    textAlign: "center",
                    my: 1,
                  }}
                >
                  {stat.value}
                </Typography>

                {/* Описание */}
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.7rem",
                    textAlign: "center",
                    mb: 1.5,
                    lineHeight: 1.3,
                    display: "block",
                  }}
                >
                  {stat.description}
                </Typography>

                {/* Източник */}
                <Box
                  sx={{
                    mt: "auto",
                    pt: 1,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.disabled,
                      fontSize: "0.65rem",
                      textAlign: "center",
                      display: "block",
                    }}
                  >
                    {stat.source}, {stat.year}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Национални сравнения */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontWeight: 600, mb: 4, textAlign: "center" }}
          >
            Национални средни стойности
          </Typography>

          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  textAlign: "center",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h3"
                  fontWeight={700}
                  color="primary"
                  gutterBottom
                  sx={{ fontSize: { xs: "1.75rem", sm: "2rem" } }}
                >
                  {nationalAverages?.stateSubsidyPerCapita || "23.37"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  лв. субсидия на човек
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  display="block"
                >
                  Средно за страната
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  textAlign: "center",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h3"
                  fontWeight={700}
                  color="success.main"
                  gutterBottom
                  sx={{ fontSize: { xs: "1.75rem", sm: "2rem" } }}
                >
                  {nationalAverages?.chitalishtaPer10kResidents || "5.5"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  читалища на 10 000 жители
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  display="block"
                >
                  Средна плътност
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  textAlign: "center",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h3"
                  fontWeight={700}
                  color="warning.main"
                  gutterBottom
                  sx={{ fontSize: { xs: "1.75rem", sm: "2rem" } }}
                >
                  {nationalAverages?.revenueFromSubsidiesPercent || "69.3"}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  приходи от държавна и общинска субсидия
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  display="block"
                >
                  Дял от общите приходи
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  textAlign: "center",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h3"
                  fontWeight={700}
                  color="secondary.main"
                  gutterBottom
                  sx={{ fontSize: { xs: "1.75rem", sm: "2rem" } }}
                >
                  {nationalAverages?.staffHigherEducationPercent || "48"}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  персонал с висше образование
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  display="block"
                >
                  От общия персонал
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Информация за методологията */}
        {/* <Paper
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.info.main, 0.03),
            border: `1px solid ${theme.palette.divider}`,
            textAlign: "center",
            maxWidth: "1000px",
            mx: "auto",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            mb={2}
          >
            <InfoOutlined color="info" />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "info.main",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              Методология на данните
            </Typography>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            paragraph
            sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
          >
            Данните са събрани от официални източници: Национален статистически
            институт (НСИ), Министерство на културата, Регистър на народните
            читалища и Регистър на обществените библиотеки. Платформата анализира
            общо {officialStatistics.totalChitalishta.toLocaleString("bg-BG")}{" "}
            читалища в България.
          </Typography>

          <Box
            display="flex"
            justifyContent="center"
            gap={1}
            flexWrap="wrap"
            mt={2}
          >
            <Chip
              label="НСИ"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
            />
            <Chip
              label="Министерство на културата"
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
            />
            <Chip
              label="Регистър на читалищата"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
            />
            <Chip
              label="Регистър на библиотеките"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
            />
          </Box>
        </Paper> */}
      </Box>
    </>
  );
};

export default HomePage;